import { useState, useEffect } from 'react';
import { getDashboardOverview, getUpcomingShowings, getBoardMembers, getVacancyRateBreakdown } from '@/lib/api';
import { useAppAuth } from '@/contexts/AppAuthContext';
import { Button } from '@/components/ui/button';
import { Plus, Users, Building2, Home, Megaphone, FileText, MessageSquare, Sofa, CalendarDays, LogOut, AlertTriangle, PieChart as PieIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { TenantEditPopup } from '@/components/TenantEditPopup';
import { IncomeBreakdownPopup } from '@/components/IncomeBreakdownPopup';
import { MaintenanceEditPopup } from '@/components/MaintenanceEditPopup';
import { MetricListPopup } from '@/components/dashboard/MetricListPopup';
import { TaskNotificationBell } from '@/components/staff/TaskNotificationBell';
import { PropertyCreatePanel } from '@/components/properties/PropertyCreatePanel';
import { UnitCreatePanel } from '@/components/properties/UnitCreatePanel';
import { TenantCreatePanel } from '@/components/tenants/TenantCreatePanel';
import { LeadCreatePanel } from '@/components/leads/LeadCreatePanel';
import { MessageBoard } from '@/components/board/MessageBoard';
import { NewMessagePanel } from '@/components/board/NewMessagePanel';
import { adminTaskNotifications, adminMarkTaskRead } from '@/lib/staffApi';

// Bell that surfaces staff-task notifications on the admin dashboard.
// Click → opens the staff tasks page (which deep-links into the task).
function DashboardTaskBell() {
  const navigate = useNavigate();
  const adminPass = typeof window !== 'undefined' ? sessionStorage.getItem('app_password') || '' : '';
  if (!adminPass) return null;
  return (
    <TaskNotificationBell
      fetcher={() => adminTaskNotifications(adminPass)}
      markRead={(id) => adminMarkTaskRead(adminPass, id)}
      onOpenTask={(t) => navigate(`/furniture-projects/staffing/tasks?task=${t.id}`)}
      className="bg-white border border-gray-200 shadow-sm"
      testId="dashboard-task-bell"
    />
  );
}

import { cardBase } from '@/components/dashboard/dashboardUtils';
import { DashboardTopMetrics } from '@/components/dashboard/DashboardTopMetrics';
import { MoveInOutWidget } from '@/components/dashboard/MoveInOutWidget';
import { HousekeepingWidget } from '@/components/dashboard/HousekeepingWidget';
import { ShowingsWidget } from '@/components/dashboard/ShowingsWidget';
import { AddShowingDialog } from '@/components/dashboard/AddShowingDialog';
import { VacanciesWidget } from '@/components/dashboard/VacanciesWidget';
import { MaintenanceWidget } from '@/components/dashboard/MaintenanceWidget';
import { FiltersWidget } from '@/components/dashboard/FiltersWidget';

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function getWeatherEmoji() {
  // Lightweight time-of-day emoji until weather API is wired up.
  const h = new Date().getHours();
  if (h < 6 || h >= 20) return '🌙';
  if (h < 10) return '⛅';
  if (h < 17) return '☀️';
  return '🌤️';
}

function getDisplayName(user) {
  if (user) {
    const first = (user.display_name || user.name || '').trim().split(/\s+/)[0];
    if (first) return first;
  }
  // Fallback to localStorage override (used before auth lands).
  try {
    const stored = (localStorage.getItem('display_name') || '').trim();
    if (stored) return stored;
  } catch { /* no-op */ }
  return 'there';
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAppAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showIncome, setShowIncome] = useState(false);
  const [showMoveOuts, setShowMoveOuts] = useState(false);
  const [showVacancies, setShowVacancies] = useState(false);
  const [showVacancyRate, setShowVacancyRate] = useState(false);
  const [vacancyRateData, setVacancyRateData] = useState(null);
  const [selectedMaint, setSelectedMaint] = useState(null);
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [showCreateProperty, setShowCreateProperty] = useState(false);
  const [showCreateUnit, setShowCreateUnit] = useState(false);
  const [showCreateLead, setShowCreateLead] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showCreateMessage, setShowCreateMessage] = useState(false);
  const [boardMembers, setBoardMembers] = useState([]);
  const [upcomingShowings, setUpcomingShowings] = useState([]);
  const [showAddShowing, setShowAddShowing] = useState(false);

  const loadDashboard = () => {
    getDashboardOverview()
      .then(setData)
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
    getUpcomingShowings().then(setUpcomingShowings).catch(() => {});
    getBoardMembers().then(setBoardMembers).catch(() => {});
  };

  useEffect(() => { loadDashboard(); }, []);

  // Lazy-load the vacancy rate breakdown the first time the user opens it.
  useEffect(() => {
    if (showVacancyRate && !vacancyRateData) {
      getVacancyRateBreakdown().then(setVacancyRateData).catch(() => setVacancyRateData(null));
    }
  }, [showVacancyRate, vacancyRateData]);

  if (loading) return <div className="flex items-center justify-center h-60 text-gray-400" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>Loading dashboard...</div>;
  if (!data) return null;

  const todayPill = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const greeting = `${getGreeting()}, ${getDisplayName(user)}!`;

  return (
    <div className="space-y-6" style={{ backgroundColor: '#fbfaf7', margin: '-1.5rem', padding: '2.25rem 2rem 3rem', minHeight: 'calc(100vh - 88px)', fontFamily: 'Inter, system-ui, sans-serif' }} data-testid="dashboard-page">

      {/* PAGE HEADER */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[28px] sm:text-[32px] font-bold tracking-tight text-gray-900 flex items-center gap-2" data-testid="dashboard-greeting">
            {greeting} <span aria-hidden="true">{getWeatherEmoji()}</span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">Here's what's happening with your properties today.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center gap-2 bg-white rounded-xl px-3.5 py-2 text-sm text-gray-700 shadow-sm border border-gray-200" data-testid="dashboard-date-pill">
            <CalendarDays className="h-4 w-4 text-gray-400" /> {todayPill}
          </div>
          <DashboardTaskBell />
          <div className="relative">
            <Button onClick={() => setShowQuickAdd(!showQuickAdd)} className="h-9 text-sm bg-emerald-700 hover:bg-emerald-800 gap-1.5" data-testid="dashboard-quick-add">
              <Plus className="h-4 w-4" /> New
            </Button>
            {showQuickAdd && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowQuickAdd(false)} />
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl shadow-lg border z-50 py-1.5 w-48" data-testid="quick-add-menu">
                  {[
                    { label: 'Property', icon: Building2, action: () => setShowCreateProperty(true), testId: 'quick-add-property' },
                    { label: 'Unit', icon: Home, action: () => setShowCreateUnit(true), testId: 'quick-add-unit' },
                    { label: 'Tenant', icon: Users, action: () => setShowCreateTenant(true), testId: 'quick-add-tenant' },
                    { label: 'Lead', icon: Megaphone, action: () => setShowCreateLead(true), testId: 'quick-add-lead' },
                    { label: 'Showing', icon: CalendarDays, action: () => setShowAddShowing(true), testId: 'quick-add-showing' },
                    { label: 'Message', icon: MessageSquare, action: () => setShowCreateMessage(true), testId: 'quick-add-message' },
                  ].map((item, i) => (
                    <button
                      key={item.label}
                      className={`w-full text-left px-3.5 py-2 text-[13px] hover:bg-gray-50 flex items-center gap-2.5 font-medium text-gray-700 ${i === 5 ? 'border-t mt-1 pt-2' : ''}`}
                      onClick={() => { item.action(); setShowQuickAdd(false); }}
                      data-testid={item.testId}
                    >
                      <item.icon className="h-4 w-4 text-gray-400" />
                      {item.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* TOP METRIC CARDS — Income chart + 3 stat cards */}
      <DashboardTopMetrics
        data={data}
        onShowIncome={() => setShowIncome(true)}
        onShowMoveOuts={() => setShowMoveOuts(true)}
        onShowVacancies={() => setShowVacancies(true)}
        onShowVacancyRate={() => setShowVacancyRate(true)}
      />

      {/* MESSAGE BOARD + MY TASKS (50/50) */}
      <div className="grid grid-cols-1 gap-5">
        <div className={`${cardBase} overflow-hidden border border-gray-200 px-4 py-4`} data-testid="dashboard-message-board">
          <MessageBoard compact />
        </div>
      </div>

      {/* MOVE-OUTS + MOVE-INS (50/50) */}
      <MoveInOutWidget moveOuts={data.move_outs_week} moveIns={data.move_ins_week} onSelectTenant={setSelectedTenant} />

      {/* UPCOMING HOUSEKEEPING (full width) */}
      <HousekeepingWidget cleanings={data.cleanings} onSelectTenant={setSelectedTenant} />

      {/* BOTTOM 3-UP: Vacancies · Showings · Maintenance + Filters */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <VacanciesWidget vacancies={data.vacancies} />
        <ShowingsWidget showings={upcomingShowings} onAddShowing={() => setShowAddShowing(true)} />
        <div className="space-y-5">
          <MaintenanceWidget maintenance={data.maintenance} onSelectMaint={setSelectedMaint} />
          <FiltersWidget filters={data.filters_due_week} />
        </div>
      </div>

      {/* Features & Furniture Buttons */}
      <div className="flex justify-center gap-3 pt-2">
        <Button variant="ghost" className="text-[13px] text-gray-400 hover:text-gray-600 gap-2 font-medium" onClick={() => navigate('/features')} data-testid="dashboard-features-button">
          <FileText className="h-4 w-4" /> Features
        </Button>
        <Button variant="ghost" className="text-[13px] text-gray-400 hover:text-gray-600 gap-2 font-medium" onClick={() => window.open('/furniture-projects', '_blank')} data-testid="dashboard-furniture-button">
          <Sofa className="h-4 w-4" /> Furniture
        </Button>
      </div>

      {/* Popups */}
      <AddShowingDialog
        open={showAddShowing}
        onOpenChange={setShowAddShowing}
        onCreated={loadDashboard}
      />
      <TenantEditPopup open={!!selectedTenant} onClose={() => setSelectedTenant(null)} tenant={selectedTenant} onUpdated={loadDashboard} />
      <IncomeBreakdownPopup open={showIncome} onClose={() => setShowIncome(false)} currentIncome={data?.current_income} />
      <MaintenanceEditPopup open={!!selectedMaint} onClose={() => setSelectedMaint(null)} request={selectedMaint} onUpdated={loadDashboard} />

      {/* Top-metric detail popups */}
      <MetricListPopup
        open={showMoveOuts} onClose={() => setShowMoveOuts(false)}
        title="Move-Outs This Week" Icon={LogOut}
        description="Tenants scheduled to move out in the next 7 days"
        items={(data?.move_outs_week || []).map((t) => ({
          id: t.id || t._id,
          primary: t.name || 'Unnamed tenant',
          secondary: `${t.property_name || 'Unknown'} · Unit ${t.unit_number || '—'}`,
          badge: t.move_out_date ? new Date(t.move_out_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '',
        }))}
        emptyMessage="No move-outs scheduled this week."
        linkLabel="Go to Move In/Out" linkPath="/move-in-out"
        testId="moveouts-popup"
      />

      <MetricListPopup
        open={showVacancies} onClose={() => setShowVacancies(false)}
        title="Upcoming Vacancies" Icon={AlertTriangle}
        description="Vacant-forward units within the next 60 days"
        items={(data?.vacancies || []).map((v) => ({
          id: `${v.unit_id}-${v.vacancy_start}`,
          primary: `${v.property_name || 'Unknown'} · Unit ${v.unit_number || '—'}`,
          secondary: v.has_future_tenant
            ? `Vacant ${v.vacancy_start} → ${v.vacancy_end || ''}`
            : `Vacant from ${v.vacancy_start}`,
          badge: v.has_future_tenant ? 'Gap' : 'Forward',
        }))}
        emptyMessage="No upcoming vacancies in the next 60 days."
        linkLabel="Go to Vacancy" linkPath="/vacancy"
        testId="vacancies-popup"
      />

      <MetricListPopup
        open={showVacancyRate} onClose={() => setShowVacancyRate(false)}
        title="Vacancy Rate Breakdown" Icon={PieIcon}
        description={vacancyRateData
          ? `Per-unit vacancy days for ${new Date(vacancyRateData.year, vacancyRateData.month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`
          : 'Loading per-unit breakdown…'}
        headerExtra={vacancyRateData ? (
          <div className="bg-violet-50 rounded-lg px-3 py-2.5 flex items-center justify-between">
            <span className="text-xs text-violet-700 font-medium">Overall this month</span>
            <span className="text-base font-bold tabular-nums text-violet-900">{vacancyRateData.overall_rate}%</span>
          </div>
        ) : null}
        items={(vacancyRateData?.units || []).map((u) => ({
          id: u.unit_id,
          primary: `${u.property_name} · Unit ${u.unit_number || '—'}`,
          secondary: `${u.vacant_days}/${u.total_days} vacant days`,
          badge: `${u.rate}%`,
        }))}
        emptyMessage="No units to report."
        linkLabel="Go to Vacancy" linkPath="/vacancy"
        testId="vacancy-rate-popup"
      />

      {showCreateTenant && <TenantCreatePanel open={showCreateTenant} onClose={() => setShowCreateTenant(false)} onCreated={() => { setShowCreateTenant(false); loadDashboard(); }} />}
      {showCreateProperty && <PropertyCreatePanel open={showCreateProperty} onClose={() => setShowCreateProperty(false)} onCreated={loadDashboard} />}
      {showCreateUnit && <UnitCreatePanel open={showCreateUnit} onClose={() => setShowCreateUnit(false)} onCreated={loadDashboard} />}
      {showCreateLead && <LeadCreatePanel open={showCreateLead} onClose={() => setShowCreateLead(false)} onCreated={loadDashboard} />}
      {showCreateMessage && <NewMessagePanel open={showCreateMessage} onClose={() => setShowCreateMessage(false)} members={boardMembers.filter(m => !m.is_archived)} onCreated={loadDashboard} />}
    </div>
  );
}
